import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Mixpanel from "./mixpanel";

const LanguageDropdown = () => {
  const { i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredLanguages, setFilteredLanguages] = useState([]);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Retrieve the language from localStorage or default to 'en'
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("appLanguage") || i18n.language || "en"
  );

  const languages = [
    { code: "ara", label: "العربية", nativeName: "Arabic", flag: "🇸🇦" },
    { code: "bn", label: "বাংলা", nativeName: "Bengali", flag: "🇧🇩" },
    { code: "zh", label: "中文", nativeName: "Chinese", flag: "🇨🇳" },
    { code: "nl", label: "Nederlands", nativeName: "Dutch", flag: "🇳🇱" },
    { code: "en", label: "English", nativeName: "English", flag: "🇺🇸" },
    { code: "fr", label: "Français", nativeName: "French", flag: "🇫🇷" },
    { code: "de", label: "Deutsch", nativeName: "German", flag: "🇩🇪" },
    { code: "gu", label: "ગુજરાતી", nativeName: "Gujarati", flag: "🇮🇳" },
    { code: "hi", label: "हिन्दी", nativeName: "Hindi", flag: "🇮🇳" },
    { code: "id", label: "Bahasa Indonesia", nativeName: "Indonesian", flag: "🇮🇩" },
    { code: "it", label: "Italiano", nativeName: "Italian", flag: "🇮🇹" },
    { code: "ja", label: "日本語", nativeName: "Japanese", flag: "🇯🇵" },
    { code: "kn", label: "ಕನ್ನಡ", nativeName: "Kannada", flag: "🇮🇳" },
    { code: "ms", label: "Bahasa Melayu", nativeName: "Malay", flag: "🇲🇾" },
    { code: "ml", label: "മലയാളം", nativeName: "Malayalam", flag: "🇮🇳" },
    { code: "mr", label: "मराठी", nativeName: "Marathi", flag: "🇮🇳" },
    { code: "es", label: "Español", nativeName: "Spanish", flag: "🇪🇸" },
    { code: "ta", label: "தமிழ்", nativeName: "Tamil", flag: "🇮🇳" },
    { code: "te", label: "తెలుగు", nativeName: "Telugu", flag: "🇮🇳" },
    { code: "th", label: "ไทย", nativeName: "Thai", flag: "🇹🇭" },
    { code: "tr", label: "Türkçe", nativeName: "Turkish", flag: "🇹🇷" },
    { code: "vi", label: "Tiếng Việt", nativeName: "Vietnamese", flag: "🇻🇳" }
  ];

  useEffect(() => {
    // Apply the language on component mount
    i18n.changeLanguage(selectedLanguage);
  }, [selectedLanguage, i18n]);

  useEffect(() => {
    // Filter languages based on search query
    const filtered = languages.filter(lang => 
      lang.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lang.nativeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lang.code.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredLanguages(filtered);
  }, [searchQuery]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Focus search input when dropdown opens
    if (dropdownOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [dropdownOpen]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
    if (!dropdownOpen) {
      setSearchQuery("");
    }
  };

  const changeLanguage = (lang) => {
    if (selectedLanguage !== lang) {
      Mixpanel.settings('Language', 'Changed', {
        old_language: selectedLanguage,
        new_language: lang,
        setting_location: 'Header Dropdown'
      });
    }

    i18n.changeLanguage(lang);
    setSelectedLanguage(lang);
    localStorage.setItem("appLanguage", lang);
    setDropdownOpen(false);
    setSearchQuery("");
  };

  const selectedLang = languages.find((lang) => lang.code === selectedLanguage);

  return (
    <div className="relative inline-flex" ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        className="group relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        aria-haspopup="menu"
        aria-expanded={dropdownOpen ? "true" : "false"}
        aria-label="Select language"
      >
        <span className="text-lg">{selectedLang?.flag}</span>
        <span className="font-medium">{selectedLang?.label}</span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${
            dropdownOpen ? "rotate-180" : ""
          }`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19 9-7 7-7-7" />
        </svg>
      </button>

      {dropdownOpen && (
        <div
          className="absolute z-50 w-80 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg animate-fade-in-fast"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="language-dropdown"
        >
          {/* Search Input */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m21 21-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search languages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Language List */}
          <div className="max-h-64 overflow-y-auto scrollbar-thin">
            {(searchQuery ? filteredLanguages : languages).map((language) => (
              <button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 ${
                  selectedLanguage === language.code
                    ? "bg-blue-50 border-r-2 border-blue-500"
                    : ""
                }`}
                role="menuitem"
              >
                <span className="text-lg flex-shrink-0">{language.flag}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 truncate">
                      {language.label}
                    </span>
                    {selectedLanguage === language.code && (
                      <svg
                        className="w-4 h-4 text-blue-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {language.nativeName}
                  </p>
                </div>
              </button>
            ))}
            
            {searchQuery && filteredLanguages.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500">
                <svg
                  className="w-8 h-8 mx-auto mb-2 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.571M15 6.343A7.962 7.962 0 0112 4c-2.34 0-4.29 1.009-5.824 2.571"
                  />
                </svg>
                <p className="text-sm">No languages found</p>
                <p className="text-xs">Try a different search term</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageDropdown;
